import * as React from "react";
import {
    Typography,
    Box,
    Grid,
    Button as MUIButton,
    Container,
    Paper,
    Modal,
    Backdrop,
    Fade,
    CircularProgress,
    Autocomplete,
    MenuItem,
    Switch,
    FormControlLabel
} from "@mui/material";
import { connect } from "react-redux";
import {
    MainInput,
    main_button,
} from "../../app/components/Styles";
import { brandFilterOptions, brandOptionLabel } from "../../app/components/BrandSelect";
import { FoCard, FoField, FoFilter, FoSearch, FoTable, statusBadge } from "./financeUi";
import BrandRateCard from "./BrandRateCard";
import { useMemo, useState } from "react";
import { getBrandServiceFees, updateBrandServiceFee, getServiceFeeTiers, getVendors } from "../../app/store/actions/whFinanceActions";
import { useEffect } from "react";
import { Edit } from "@mui/icons-material";
import { notification } from "antd";

export const BrandServiceFees = ({
    getBrandServiceFees,
    updateBrandServiceFee,
    getServiceFeeTiers,
    getVendors,
    serviceFees,
    tiers,
    vendors,
    isLoading
}) => {
    const [query, setQuery] = useState("");
    const [selectedVendor, setSelectedVendor] = React.useState(null);
    const [editModalOpen, setEditModalOpen] = React.useState(false);
    const [updating, setUpdating] = React.useState(false);
    const [selectedBrand, setSelectedBrand] = React.useState(null);
    const [formData, setFormData] = React.useState({
        vendorId: null,
        tierId: null,
        dynamicFees: false
    });

    useEffect(() => {
        getBrandServiceFees();
        getServiceFeeTiers();
        getVendors();
    }, [getBrandServiceFees, getServiceFeeTiers, getVendors]);

    const handleFilter = () => {
        getBrandServiceFees({ vendorId: selectedVendor?._id });
    };

    const handleOpenEditModal = (record) => {
        const vendor = vendors?.find(v => v.brandName === record.brand);
        const tier = tiers?.find(t => t.name === record.tier);
        setSelectedBrand(record);
        setFormData({
            vendorId: vendor?._id || null,
            tierId: tier?._id || null,
            dynamicFees: record.dynamicFees || false
        });
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        if (!updating) {
            setEditModalOpen(false);
            setSelectedBrand(null);
            setFormData({
                vendorId: null,
                tierId: null,
                dynamicFees: false
            });
        }
    };

    const handleUpdateBrandFee = () => {
        if (!formData.vendorId || !formData.tierId) {
            notification.error({ message: "Please select vendor and tier" });
            return;
        }

        setUpdating(true);
        updateBrandServiceFee({
            vendorId: formData.vendorId,
            tierId: formData.tierId,
            dynamicFees: formData.dynamicFees
        }, undefined);
        // Close modal and refresh data
        setUpdating(false);
        setEditModalOpen(false);
        getBrandServiceFees({ vendorId: selectedVendor?._id });
        setSelectedBrand(null);
        setFormData({
            vendorId: null,
            tierId: null,
            dynamicFees: false
        });
    };

    const columns = [
        {
            title: 'Brand',
            dataIndex: 'brand',
            sorter: (a, b) => a.brand.localeCompare(b.brand),
        },
        {
            title: 'Tier',
            dataIndex: 'tier',
            render: (tier) => statusBadge(tier),
            sorter: (a, b) => a.tier.localeCompare(b.tier),
        },
        {
            title: 'Chainz Fee',
            dataIndex: 'chainzFee',
            render: (_, { chainzFee }) => `${chainzFee} EGP`,
            sorter: (a, b) => a.chainzFee - b.chainzFee,
        },
        {
            title: 'Shipping Multiplier',
            dataIndex: 'shippingMultiplier',
            render: (_, { shippingMultiplier }) => `${shippingMultiplier}x`,
            sorter: (a, b) => a.shippingMultiplier - b.shippingMultiplier,
        },
        {
            title: 'Dynamic Fees',
            dataIndex: 'dynamicFees',
            render: (_, { dynamicFees }) => statusBadge(dynamicFees ? "Yes" : "No"),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <MUIButton
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleOpenEditModal(record)}
                    sx={{
                        textTransform: 'none',
                        borderColor: 'var(--secColor)',
                        color: 'var(--secColor)',
                        '&:hover': {
                            borderColor: 'var(--secColor)',
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    Edit
                </MUIButton>
            ),
        },
    ];

    const serviceFeesData = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return (Array.isArray(serviceFees) ? serviceFees : [])
            .map((fee) => ({
                _id: fee.brand,
                brand: fee.brand,
                tier: fee.tier,
                chainzFee: fee.chainzFee,
                shippingMultiplier: fee.shippingMultiplier,
                dynamicFees: fee.dynamicFees,
            }))
            .filter((fee) => !needle || String(fee.brand || "").toLowerCase().includes(needle));
    }, [serviceFees, query]);

    return (
        <div className="fo-stack">
            <div className="fo-tools">
                <FoSearch value={query} onChange={setQuery} placeholder="Search brand..." />
            </div>
            <FoFilter onApply={handleFilter} applying={isLoading}>
                <FoField label="Vendor" grow>
                    <Autocomplete
                        options={vendors || []}
                        value={selectedVendor}
                        onChange={(_, value) => setSelectedVendor(value)}
                        getOptionLabel={brandOptionLabel}
                        filterOptions={brandFilterOptions}
                        renderInput={(params) => (
                            <MainInput {...params} fullWidth placeholder="Select vendor" />
                        )}
                    />
                </FoField>
            </FoFilter>
            <FoCard title="Brand service fees" count={`${serviceFeesData.length} brands`}>
                <FoTable
                    columns={columns}
                    dataSource={serviceFeesData}
                    loading={isLoading}
                    rowKey="_id"
                    empty="No brands match this view."
                    pagination={{ pageSize: 10, total: serviceFeesData.length }}
                />
            </FoCard>
            <BrandRateCard />

            {/* Edit Brand Fee Modal */}
            <Modal
                open={editModalOpen}
                onClose={handleCloseEditModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Fade in={editModalOpen}>
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 500 },
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            outline: 'none',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "var(--secColor)" }}>
                            Edit Brand Service Fee
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Brand *
                            </Typography>
                            <Autocomplete
                                options={vendors || []}
                                value={vendors?.find(v => v._id === formData.vendorId) || null}
                                onChange={(_, value) => setFormData(prev => ({ ...prev, vendorId: value?._id || null }))}
                                getOptionLabel={brandOptionLabel}
                                filterOptions={brandFilterOptions}
                                renderInput={(params) => (
                                    <MainInput
                                        {...params}
                                        fullWidth
                                        placeholder="Select Brand"
                                        disabled={updating}
                                    />
                                )}
                                disabled={updating}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Tier *
                            </Typography>
                            <Autocomplete
                                options={tiers || []}
                                value={tiers?.find(t => t._id === formData.tierId) || null}
                                onChange={(_, value) => setFormData(prev => ({ ...prev, tierId: value?._id || null }))}
                                getOptionLabel={(tier) => tier.name}
                                renderInput={(params) => (
                                    <MainInput
                                        {...params}
                                        fullWidth
                                        placeholder="Select Tier"
                                        disabled={updating}
                                    />
                                )}
                                disabled={updating}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.dynamicFees}
                                        onChange={(e) => setFormData(prev => ({ ...prev, dynamicFees: e.target.checked }))}
                                        disabled={updating}
                                    />
                                }
                                label="Dynamic Fees"
                            />
                        </Box>

                        {updating && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <CircularProgress size={24} sx={{ mr: 2, color: 'var(--secColor)' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Updating...
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                            <MUIButton
                                onClick={handleCloseEditModal}
                                disabled={updating}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </MUIButton>
                            <MUIButton
                                onClick={handleUpdateBrandFee}
                                disabled={updating}
                                sx={{ ...main_button, textTransform: 'none' }}
                            >
                                {updating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Update'}
                            </MUIButton>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => ({
    isLoading: state?.records?.isLoading || state?.wait?.isLoading,
    serviceFees: state?.records?.serviceFees,
    tiers: state?.records?.tiers,
    vendors: state?.records?.vendors
});

const mapDispatchToProps = { getBrandServiceFees, updateBrandServiceFee, getServiceFeeTiers, getVendors };

export default connect(mapStateToProps, mapDispatchToProps)(BrandServiceFees);
